import { useEffect, useRef, Dispatch, SetStateAction, useState } from 'react';
import { FormHandles } from '@unform/core';
import { useNavigate } from 'react-router-dom';

import {
  listSelectorProject,
  statusesOptionsDashboard,
} from '@modules/projects/helpers/project';

import {
  SEARCH_TYPES,
  SORT_TYPES,
  ALL_PERMISSION_KEYS,
} from '@shared/constants/shared';
import {
  BASE_URL,
  PROJECT_PER_PAGE,
  STATUSES,
} from '@modules/projects/constants/project';

import { useProject } from '@modules/projects/hooks/project';

import { listValidatorSchema } from '@modules/projects/validators/project';
import { ToListFilter } from '@modules/projects/sanitizers/project';

import {
  TFilterByProps,
  TProjectObject,
} from '@modules/projects/@types/TProject';
import { TStageNameObject } from '@modules/templates/@types/TStageName';
import { TUserObject } from '@modules/users/@types/TUser';
import { TSharedListValues } from '@shared/@types/TShared';
import { TDashboardRelationsObject } from '@modules/projects/@types/TDashboard';

import { listSelectorStageName } from '@modules/projects/helpers/projectStage';
import { listSelectorUser } from '@modules/projects/helpers/users';

import { safetyJSONParse } from '@shared/utils/json';

import {
  Container,
  IconButton,
  AddIcon,
  SearchIcon,
  CircularProgress,
  Typography,
  FormContainer,
  FormGridContainer,
  TextInput,
  FormSelector,
  IconButtonContainer,
  HasPermissionComponent,
} from './styles';

type TProjectListHeader = {
  setIsFilterList: Dispatch<SetStateAction<boolean>>;
  setFilterData: Dispatch<SetStateAction<TSharedListValues[]>>;
  setIsReloaded: Dispatch<SetStateAction<boolean>>;
  isReloaded: boolean;
  formInitialData?: TDashboardRelationsObject;
};

type TSelectorsValues = {
  project_uuid?: TProjectObject | null;
  stage_name_uuid?: TStageNameObject | null;
  status?: { key: STATUSES; label: string } | null;
  responsible_uuid?: TUserObject | null;
  document?: string;
};

export function ProjectListHeader({
  setIsFilterList,
  setFilterData,
  setIsReloaded,
  isReloaded,
  formInitialData,
}: TProjectListHeader): JSX.Element {
  const navigate = useNavigate();
  const { list, listLoading, validations } = useProject();

  const formRef = useRef<FormHandles>(null);

  const [selectorsValues, setSelectorsValues] = useState<TSelectorsValues>({});

  function onSubmitSearch({
    status,
    project_uuid,
    stage_name_uuid,
    responsible_uuid,
    document,
  }: TFilterByProps): void {
    const parsedData = [];

    if (status && stage_name_uuid) {
      parsedData.push({
        field: 'project_stages',
        type: SEARCH_TYPES.relation,
        values: [
          {
            field: 'stage_name_uuid',
            type: SEARCH_TYPES.normal,
            values: [stage_name_uuid],
          },
          {
            field: 'status',
            type: SEARCH_TYPES.normal,
            values: [status],
          },
        ],
      });
    } else if (stage_name_uuid) {
      parsedData.push({
        field: 'project_stages',
        type: SEARCH_TYPES.relation,
        values: [
          {
            field: 'stage_name_uuid',
            type: SEARCH_TYPES.normal,
            values: [stage_name_uuid],
          },
        ],
      });
    } else if (status) {
      parsedData.push({
        field: 'project_stages',
        type: SEARCH_TYPES.relation,
        values: [
          {
            field: 'status',
            type: SEARCH_TYPES.normal,
            values: [status],
          },
        ],
      });
    }

    if (stage_name_uuid && responsible_uuid) {
      parsedData.push({
        field: 'project_stages',
        type: SEARCH_TYPES.relation,
        values: [
          {
            field: 'stage_name_uuid',
            type: SEARCH_TYPES.normal,
            values: [stage_name_uuid],
          },
          {
            field: 'responsible_uuid',
            type: SEARCH_TYPES.normal,
            values: [responsible_uuid],
          },
        ],
      });
    }

    if (project_uuid)
      parsedData.push({
        field: 'uuid',
        type: SEARCH_TYPES.normal,
        values: [project_uuid],
      });

    if (responsible_uuid) {
      parsedData.push({
        field: 'project_stages',
        type: SEARCH_TYPES.relation,
        values: [
          {
            field: 'responsible_uuid',
            type: SEARCH_TYPES.normal,
            values: [responsible_uuid],
          },
        ],
      });
    }

    if (document)
      parsedData.push({
        field: 'document',
        type: SEARCH_TYPES.normal,
        values: [document],
      });

    const searchData = {
      order: SORT_TYPES.desc,
      order_by: 'updated_at',
      page: 1,
      per_page: PROJECT_PER_PAGE,
      for_selector: false,
      data: parsedData,
    };

    const documentField = formRef.current?.getFieldValue('document');
    const fieldsValues = {
      document: documentField,
      ...selectorsValues,
    };

    list(searchData);
    setFilterData(parsedData);
    setIsFilterList(true);
    localStorage.setItem('dashboardFilters', JSON.stringify(fieldsValues));
  }

  function addFunction() {
    navigate(`/${BASE_URL}/form`, { state: { creating: true } });
  }

  useEffect(() => {
    const localStorageDashboardFilters = safetyJSONParse(
      localStorage.getItem('dashboardFilters'),
    );

    const localStorageDashboardFiltersWithType =
      localStorageDashboardFilters as Record<string, unknown>;

    const projectUuidFieldRef = formRef.current?.getFieldRef('project_uuid');
    const stageNameUuidFieldRef =
      formRef.current?.getFieldRef('stage_name_uuid');
    const statusFieldRef = formRef.current?.getFieldRef('status');
    const responsibleUuidFieldRef =
      formRef.current?.getFieldRef('responsible_uuid');

    projectUuidFieldRef?.handleChange(
      localStorageDashboardFiltersWithType?.project_uuid,
    );
    stageNameUuidFieldRef?.handleChange(
      localStorageDashboardFiltersWithType?.stage_name_uuid,
    );
    statusFieldRef?.handleChange(localStorageDashboardFiltersWithType?.status);
    responsibleUuidFieldRef?.handleChange(
      localStorageDashboardFiltersWithType?.responsible_uuid,
    );
    formRef.current?.setFieldValue(
      'document',
      localStorageDashboardFiltersWithType?.document,
    );

    if (isReloaded) {
      localStorage.removeItem('dashboardFilters');
      formRef?.current?.reset();
    }

    formRef.current?.setErrors(validations || {});
  }, [validations, isReloaded]);

  return (
    <Container>
      <Typography>Projetos</Typography>
      <HasPermissionComponent
        permission_key={[ALL_PERMISSION_KEYS.project_create]}
        element={
          <IconButton onClick={addFunction}>
            <AddIcon />
          </IconButton>
        }
      />

      <FormContainer<TFilterByProps>
        formRef={formRef}
        validatorSchema={listValidatorSchema}
        sanitizer={ToListFilter}
        onSubmit={onSubmitSearch}
        initialData={{
          project_uuid: formRef.current?.getFieldValue('project_uuid'),
        }}
      >
        <FormGridContainer justifyContent="right" gap={1}>
          <FormSelector<TProjectObject>
            label="Projeto:"
            name="project_uuid"
            idColumn="uuid"
            nameColumn="name"
            handleSearch={listSelectorProject}
            options={(formInitialData?.projects || []) as TProjectObject[]}
            onChange={project_uuid => {
              setIsReloaded(false);
              setSelectorsValues(oldData => {
                if (!project_uuid) {
                  const auxOldData = oldData;
                  delete auxOldData.project_uuid;
                  return { ...auxOldData };
                }
                return { ...oldData, project_uuid };
              });
            }}
            disabled={listLoading}
            gridProps={{ xs: 12, sm: 2 }}
          />
          <FormSelector<TStageNameObject>
            label="Etapa:"
            name="stage_name_uuid"
            idColumn="uuid"
            nameColumn="name"
            handleSearch={listSelectorStageName}
            options={(formInitialData?.stage_names || []) as TStageNameObject[]}
            onChange={stage_name_uuid => {
              setIsReloaded(false);
              setSelectorsValues(oldData => {
                if (!stage_name_uuid) {
                  const auxOldData = oldData;
                  delete auxOldData.stage_name_uuid;
                  return { ...auxOldData };
                }
                return { ...oldData, stage_name_uuid };
              });
            }}
            disabled={listLoading}
            gridProps={{ xs: 12, sm: 2 }}
          />
          <FormSelector
            label="Status:"
            name="status"
            idColumn="key"
            nameColumn="label"
            options={statusesOptionsDashboard}
            onChange={status => {
              setIsReloaded(false);
              setSelectorsValues(oldData => {
                if (!status) {
                  const auxOldData = oldData;
                  delete auxOldData.status;
                  return { ...auxOldData };
                }
                return { ...oldData, status };
              });
            }}
            disabled={listLoading}
            gridProps={{ xs: 12, sm: 2 }}
          />
          <FormSelector<TUserObject>
            label="Dirigente:"
            name="responsible_uuid"
            idColumn="uuid"
            handleSearch={listSelectorUser}
            nameColumn="name"
            options={(formInitialData?.responsibles || []) as TUserObject[]}
            onChange={responsible_uuid => {
              setIsReloaded(false);
              setSelectorsValues(oldData => {
                if (!responsible_uuid) {
                  const auxOldData = oldData;
                  delete auxOldData.responsible_uuid;
                  return { ...auxOldData };
                }
                return { ...oldData, responsible_uuid };
              });
            }}
            disabled={listLoading}
            gridProps={{ xs: 12, sm: 2 }}
          />
          <TextInput label="Documento" name="document" disabled={listLoading} />
          <IconButtonContainer>
            <IconButton type="submit" disabled={listLoading}>
              {listLoading ? <CircularProgress size={26} /> : <SearchIcon />}
            </IconButton>
          </IconButtonContainer>
        </FormGridContainer>
      </FormContainer>
    </Container>
  );
}

ProjectListHeader.defaultProps = {
  formInitialData: undefined,
};
